import { Button, Tooltip, Typography } from "antd";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import { GridType, ICellRendererParams, ValueFormatterParams } from "../../../../types/grid";
import { getGridSettings } from "../../../../utils/globalService";
import { PROPERTY_STATUS, PropertyStatus } from "../../../libs/constants";
import { convertToTitleCase, formatCurrency, formatNumber, getStatusLabel, withUnit } from "../../../libs/utils";
import { unitBookingStatus } from "../../BookingForm/components/utils";
import { Unit } from "../types";
import { defaultColumns } from "./columns";
import { UnitGridData } from "./types";
import { rentCellRenderer, withFallback } from "./utils";

export type UseUnitGridConfigOptions<TData = unknown> = {
  actionCellRenderer?: (params: ICellRendererParams<TData>) => ReactNode;
  resetGrid?: boolean;
  setGridReset?: (value: boolean) => void;
};

export const useUnitGridConfig = (options: UseUnitGridConfigOptions) => {
  const { actionCellRenderer, resetGrid, setGridReset } = options;

  const navigate = useNavigate();

  const [columns, setColumns] = useState<GridType[]>(defaultColumns);

  useEffect(() => {
    getGridSettings("units", defaultColumns, resetGrid).then((response) => {
      setColumns(response);
      setGridReset(false);
    });
  }, [resetGrid]);

  const unitsList = useSelector((state: any) => state.units.unitsList);

  const gridData: UnitGridData[] = useMemo(() => {
    if (!unitsList?.data) return [];

    return unitsList.data.map((item: Unit) => ({
      unitId: item.unitId,
      unitStatus: withFallback(item.unitStatus),
      unitSubStatus: item.leaseStatus,
      unitNo: withFallback(item.unit?.unitCode),
      unitType: withFallback(item.unit?.unitType),
      unitSubType: withFallback(item.unit?.unitSubType),
      propertyName: withFallback(item.propertyName),
      address: withFallback([item.address?.community, item.address?.subCommunity].filter(Boolean).join(", ")),
      areaSqft: item.unit?.areaSqft,
      suiteSize: item.unit?.suiteSize,
      approvedAnnualRent: item.approvedAnnualRent,
      blockedReason: withFallback(item.blockData?.reason),
      propertyAdminEmail: withFallback(item.propertyAdmin?.email),
      ownerEmail: withFallback(item.owner?.email),
      ownerNationality: withFallback(item.owner?.nationality),
      ownerNumber: withFallback(item.owner?.number),
      ownerPhone: withFallback(item.owner?.phone),
      ownerName: withFallback(item.owner?.name),
      ownerAddress: withFallback(item.owner?.address),
      bookingCount: item.bookingCount,
      recordNumber: item?.recordNumber,
      record: item,
    }));
  }, [unitsList]);

  const formatGridColumns = (defaultColumns: GridType[]): GridType[] => {
    return defaultColumns.map((column) =>
      match(column.field)
        .with("unitStatus", () => ({
          ...column,
          maxWidth: 100,
          cellRenderer: (params: ICellRendererParams<UnitGridData, PropertyStatus>) => {
            const status = params.value;
            const isBlocked = params.data.record?.blockData?.block;

            const statusClassMap: Partial<Record<PropertyStatus, string>> = {
              [PROPERTY_STATUS.DISABLED]: "inactive",
              [PROPERTY_STATUS.RENTED]: "inactive",
              [PROPERTY_STATUS.RESERVED]: "inactive-orange",
            };

            const className = `statusUnit
              ${statusClassMap[status] || ""}
              ${isBlocked ? "inactive-block" : ""}
            `.trim();

            return (
              <Typography.Text className={className}>{getStatusLabel(isBlocked ? "blocked" : status)}</Typography.Text>
            );
          },
        }))
        .with("unitSubStatus", () => ({
          ...column,
          minWidth: 270,
          valueFormatter: (params: ValueFormatterParams<UnitGridData, UnitGridData["unitSubStatus"]>) => {
            const leaseStatus = params.value;
            const unitStatus = params.data?.unitStatus;
            if (unitStatus !== PROPERTY_STATUS.RESERVED || !leaseStatus) return "--";
            return `${unitBookingStatus(leaseStatus)} - ${convertToTitleCase(leaseStatus)}`;
          },
        }))
        .with("unitType", () => ({ ...column, minWidth: 100 }))
        .with("unitSubType", () => ({ ...column, minWidth: 200 }))
        .with("areaSqft", () => ({
          ...column,
          maxWidth: 130,
          type: "rightAligned",
          valueFormatter: (params: ValueFormatterParams<UnitGridData, UnitGridData["areaSqft"]>) =>
            params.value ? withUnit(formatNumber(params.value), "sqft") : "--",
        }))
        .with("suiteSize", () => ({
          ...column,
          maxWidth: 130,
          type: "rightAligned",
          valueFormatter: (params: ValueFormatterParams<UnitGridData, UnitGridData["suiteSize"]>) =>
            params.value ? withUnit(formatNumber(params.value), "sqft") : "--",
        }))
        .with("approvedAnnualRent", () => ({
          ...column,
          type: "rightAligned",
          cellRenderer: (params: ICellRendererParams<UnitGridData, UnitGridData["approvedAnnualRent"]>) =>
            params.value ? formatCurrency(params.value) : "--",
        }))
        .with("rent1Installment", () => ({ ...column, type: "rightAligned", cellRenderer: rentCellRenderer(1) }))
        .with("rent2Installment", () => ({ ...column, type: "rightAligned", cellRenderer: rentCellRenderer(2) }))
        .with("rent3Installment", () => ({ ...column, type: "rightAligned", cellRenderer: rentCellRenderer(3) }))
        .with("rent4Installment", () => ({ ...column, type: "rightAligned", cellRenderer: rentCellRenderer(4) }))
        .with("rent6Installment", () => ({ ...column, type: "rightAligned", cellRenderer: rentCellRenderer(6) }))
        .with("rent12Installment", () => ({ ...column, type: "rightAligned", cellRenderer: rentCellRenderer(12) }))
        .with("bookingCount", () => ({
          ...column,
          maxWidth: 150,
          cellRenderer: (params: ICellRendererParams<UnitGridData, UnitGridData["bookingCount"]>) => {
            const bookingCount = params.value;
            if (!bookingCount) return null;
            return (
              <Tooltip title="View bookings" trigger="hover" showArrow={false}>
                <Button onClick={() => navigate(`/bookings`, { state: { unitId: params?.data?.unitNo } })} type="link">
                  {bookingCount} ↗
                </Button>
              </Tooltip>
            );
          },
        }))
        .with("action", () => ({
          ...column,
          maxWidth: 200,
          pinned: "right" as const,
          cellRenderer: actionCellRenderer,
        }))
        .with("recordNumber", () => ({
          ...column,
          maxWidth: 200,
          // pinned: "right" as const,
          cellRenderer: (data) => {
            return data?.data?.recordNumber;
          },
        }))
        .otherwise(() => column)
    );
  };

  return { columns, gridData, setColumns, formatGridColumns };
};
